/**
 * MemorizProgress widget -- Memorization progress tracker.
 * Real data via /aweni/api/v1/gateway/memoriz/api/stats. Skeleton (NOT mock)
 * fallback on 401/404/network errors so a guest or new user never sees
 * fictional surahs/progress on the home screen.
 */

import type { Widget, WidgetContext } from '../types';

interface MemorizData {
  surahsStarted: number;
  ayahsMemorized: number;
  currentSurah: string;
  currentProgress: number;
  reviewStreak: number;
  nextReviewDue: string;
  cardsDue: number;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? value as Record<string, unknown> : null;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function parseMemorizData(payload: unknown): MemorizData {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root ?? {};
  const currentSurah = typeof data.currentSurah === 'string'
    ? data.currentSurah
    : typeof data.current_surah === 'string'
      ? data.current_surah
      : '';
  const nextReviewDue = typeof data.nextReviewDue === 'string'
    ? data.nextReviewDue
    : typeof data.next_review_due === 'string'
      ? data.next_review_due
      : '';

  return {
    surahsStarted: toNumber(data.surahsStarted ?? data.surahs_started),
    ayahsMemorized: toNumber(data.ayahsMemorized ?? data.ayahs_memorized),
    currentSurah,
    currentProgress: toNumber(data.currentProgress ?? data.current_progress),
    reviewStreak: toNumber(data.reviewStreak ?? data.review_streak ?? data.streak),
    nextReviewDue,
    cardsDue: toNumber(data.cardsDue ?? data.cards_due),
  };
}

function hasMemorizData(data: MemorizData): boolean {
  return data.surahsStarted > 0
    || data.ayahsMemorized > 0
    || data.currentProgress > 0
    || data.reviewStreak > 0
    || data.cardsDue > 0
    || data.currentSurah.trim().length > 0;
}

function getErrorStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const e = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof e.status === 'number') return e.status;
  if (typeof e.response?.status === 'number') return e.response.status;
  return null;
}

function isSkeletonFallbackError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 401 || status === 404) return true;
  if (error instanceof TypeError) return true;
  const msg = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : String(error ?? '');
  return /network|failed to fetch|load failed|offline|timeout|http\s*401|http\s*404/i.test(msg);
}

export const MemorizProgress: Widget = {
  id: 'memoriz-progress',
  label: { en: 'Memorization', fr: 'Memorisation', ar: 'الحفظ', es: 'Memorizacion', de: 'Auswendiglernen' },
  icon: '📚',
  defaultVisible: true,
  defaultOrder: 7,
  defaultSize: 'compact',

  visibilityRule: () => {
    try {
      const user = JSON.parse(localStorage.getItem('aweni_user') || '{}');
      return user.prayerEnabled !== false;
    } catch { return true; }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const totalAyahs = 6236;
    const barOrigin = ctx.isRtl ? 'right' : 'left';

    const renderSkeleton = () => {
      if (size === 'compact') {
        container.innerHTML = `
          <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
            <div class="h-4 w-3/4 rounded mb-3 skeleton-pulse" style="background:var(--bg-secondary);"></div>
            <div class="h-3 w-1/3 rounded skeleton-pulse" style="background:var(--bg-secondary);"></div>
          </div>`;
      } else {
        container.innerHTML = `
          <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
            <div class="h-3 w-28 rounded mb-4 skeleton-pulse" style="background:var(--bg-secondary);"></div>
            <div class="grid grid-cols-2 gap-3 mb-3">
              <div class="h-14 rounded-xl skeleton-pulse" style="background:var(--bg-secondary);"></div>
              <div class="h-14 rounded-xl skeleton-pulse" style="background:var(--bg-secondary);"></div>
            </div>
            <div class="h-3 rounded mb-3 skeleton-pulse" style="background:var(--bg-secondary);"></div>
            <div class="h-3 rounded w-2/3 skeleton-pulse" style="background:var(--bg-secondary);"></div>
          </div>`;
      }
    };

    const renderData = (data: MemorizData) => {
      const safeSurah = escapeHtml(data.currentSurah);
      const safeReviewDue = escapeHtml(data.nextReviewDue);

      if (size === 'compact') {
        container.innerHTML = `
          <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="memoriz">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span>📚</span>
                <span class="text-sm" style="color:var(--text-primary);">
                  ${ctx.t('widget_memorizing')}: ${safeSurah} · ${data.currentProgress}%
                </span>
              </div>
              <span class="text-xs" style="color:var(--text-secondary);">${data.cardsDue} ${ctx.t('widget_cards_due_short')}</span>
            </div>
          </div>`;
      } else {
        container.innerHTML = `
          <div class="rounded-2xl p-5 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="memoriz">
            <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">${ctx.t('widget_memoriz_title')}</h3>

            <div class="grid grid-cols-2 gap-3 mb-3">
              <div class="rounded-xl p-3" style="background:var(--bg-secondary);">
                <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_surahs_started')}</p>
                <p class="text-lg font-bold" style="color:var(--text-primary);">${data.surahsStarted}/114</p>
              </div>
              <div class="rounded-xl p-3" style="background:var(--bg-secondary);">
                <p class="text-xs" style="color:var(--text-secondary);">${ctx.t('widget_ayahs_memorized')}</p>
                <p class="text-lg font-bold" style="color:var(--text-primary);">${data.ayahsMemorized}/${totalAyahs}</p>
              </div>
            </div>

            <div class="mb-3">
              <div class="flex items-center justify-between mb-1">
                <span class="text-xs font-medium" style="color:var(--text-primary);">${safeSurah}</span>
                <span class="text-xs" style="color:var(--text-secondary);">${data.currentProgress}%</span>
              </div>
              <div class="rounded-full overflow-hidden" style="height:6px;background:var(--bg-secondary);">
                <div class="rounded-full" style="height:100%;width:100%;background:#F59E0B;transform:scaleX(${data.currentProgress / 100});transform-origin:${barOrigin};transition:transform var(--motion-duration-slow) var(--motion-easing-standard);"></div>
              </div>
            </div>

            <div class="flex items-center justify-between text-xs">
              <span style="color:var(--text-secondary);">🔥 ${ctx.t('widget_review_streak')}: ${data.reviewStreak} ${ctx.t('widget_days')}</span>
              <span style="color:var(--accent);">${data.cardsDue} ${ctx.t('widget_cards_due_short')} ${ctx.t('widget_in_time', { time: safeReviewDue })}</span>
            </div>
          </div>`;
      }

      container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
        el.addEventListener('click', () => ctx.navigate('memoriz'));
      });
    };

    renderSkeleton();

    void (async () => {
      try {
        const resp = await fetch('/aweni/api/v1/gateway/memoriz/api/stats', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!resp.ok) {
          if (resp.status === 401 || resp.status === 404) return;
          throw Object.assign(new Error(`HTTP ${resp.status}`), { status: resp.status });
        }
        const payload = await resp.json();
        const data = parseMemorizData(payload);
        if (!hasMemorizData(data)) return;
        renderData(data);
      } catch (error: unknown) {
        if (isSkeletonFallbackError(error)) return;
        console.warn('[MemorizProgress] unexpected stats error:', error);
      }
    })();
  },
};
